<template>
  <div class="architecture-demo">
    <!-- Navigation -->
    <el-breadcrumb separator="/" class="breadcrumb">
      <el-breadcrumb-item :to="{ path: '/welcome' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>架构演示</el-breadcrumb-item>
      <el-breadcrumb-item>并发购票实验室</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- Main Content -->
    <el-card class="main-card" shadow="hover">
      <div slot="header" class="clearfix header-row">
        <span class="page-title">
          <i class="el-icon-s-platform"></i> 高并发购票实验室
        </span>
        <el-tag type="info" effect="dark" size="small">Architecture Demo Lab</el-tag>
      </div>

      <!-- Top Section: Config & Controls -->
      <el-row :gutter="20" class="control-section">
        <el-col :span="8">
          <div class="panel-card config-panel">
            <div class="panel-title"><i class="el-icon-setting"></i> 实验参数配置</div>
            <el-form label-position="left" label-width="100px" size="small">
              <el-form-item label="排片ID">
                <el-input-number v-model="config.sessionId" :min="1" style="width: 100%;" @change="fetchSessionInfo"></el-input-number>
              </el-form-item>
              <!-- 排片信息展示 -->
              <div v-if="sessionInfo.loaded" class="session-info-box">
                <div v-if="sessionInfo.valid" class="session-info-content">
                  <div class="session-info-item">
                    <i class="el-icon-film"></i>
                    <span class="info-label">电影名称：</span>
                    <span class="info-value">{{ sessionInfo.movieName }}</span>
                  </div>
                  <div class="session-info-item">
                    <i class="el-icon-time"></i>
                    <span class="info-label">放映时间：</span>
                    <span class="info-value">{{ sessionInfo.playTime }}</span>
                  </div>
                  <div class="session-info-item">
                    <i class="el-icon-location-outline"></i>
                    <span class="info-label">放映厅：</span>
                    <span class="info-value">{{ sessionInfo.hallName }}</span>
                  </div>
                </div>
                <div v-else class="session-info-error">
                  <i class="el-icon-warning"></i> 未找到该排片信息
                </div>
              </div>
              <el-form-item label="抢购座位">
                <el-input v-model="config.seatInfo" placeholder="e.g. 1排1座"></el-input>
              </el-form-item>
              <el-form-item label="并发数量">
                <el-input-number v-model="config.concurrency" :min="1" :max="100" style="width: 100%;"></el-input-number>
              </el-form-item>
              <el-divider></el-divider>
              <el-button type="warning" plain icon="el-icon-refresh-left" style="width: 100%" @click="cleanSeats">
                重置测试数据 (Clean Seats)
              </el-button>
              <p class="config-tip">提示：重置操作会将该场次的已售数清零，并恢复初始座位图。</p>
            </el-form>
          </div>
        </el-col>

        <el-col :span="16">
          <div class="panel-card action-panel">
            <div class="panel-title"><i class="el-icon-cpu"></i> 压力测试控制台</div>
            <div class="action-buttons">
              <div class="action-group">
                <el-button 
                  class="launch-btn sync-btn" 
                  type="danger" 
                  :loading="loading"
                  @click="startTest('sync')">
                  <div class="btn-content">
                    <i class="el-icon-timer"></i>
                    <span class="btn-title">发起同步抢购</span>
                    <span class="btn-subtitle">Traditional Mode</span>
                  </div>
                </el-button>
                <p class="mode-desc">同步请求直接锁表/写库，高并发下易产生超卖或数据库死锁。</p>
              </div>

              <div class="action-group">
                <el-button 
                  class="launch-btn async-btn" 
                  type="success" 
                  :loading="loading"
                  @click="startTest('async')">
                  <div class="btn-content">
                    <i class="el-icon-lightning"></i>
                    <span class="btn-title">发起异步抢购</span>
                    <span class="btn-subtitle">Queue Mode</span>
                  </div>
                </el-button>
                <p class="mode-desc">异步请求进入内存队列，后台顺序消费，削峰填谷，彻底杜绝超卖。</p>
              </div>
            </div>
          </div>
        </el-col>
      </el-row>

      <!-- Middle Section: Dashboard Stats -->
      <el-row :gutter="20" class="stats-section">
        <el-col :span="6">
          <div class="stat-card total">
            <div class="stat-label">Total Requests</div>
            <div class="stat-value">{{ stats.total }}</div>
            <i class="el-icon-s-data stat-icon"></i>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card success">
            <div class="stat-label">Successful</div>
            <div class="stat-value">{{ stats.success }}</div>
            <i class="el-icon-circle-check stat-icon"></i>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card fail">
            <div class="stat-label">Failed / Conflict</div>
            <div class="stat-value">{{ stats.fail }}</div>
            <i class="el-icon-warning-outline stat-icon"></i>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card time">
            <div class="stat-label">Avg Response Time</div>
            <div class="stat-value">{{ stats.avgTime }} <span class="unit">ms</span></div>
            <i class="el-icon-time stat-icon"></i>
          </div>
        </el-col>
      </el-row>

      <!-- Bottom Section: Real-time Console -->
      <div class="console-section">
        <div class="console-header">
          <span><i class="el-icon-monitor"></i> Real-time Execution Log</span>
          <el-button type="text" size="mini" @click="clearLogs" style="color: #fff;">Clear Console</el-button>
        </div>
        <div class="console-body" ref="consoleBody">
          <div v-if="logs.length === 0" class="console-placeholder">> Ready to simulate concurrency...</div>
          <div v-for="(log, index) in logs" :key="index" class="log-line">
            <span class="log-time">[{{ log.time }}]</span>
            <span :class="['log-tag', log.type]">{{ log.tag }}</span>
            <span class="log-msg">{{ log.message }}</span>
            <span v-if="log.duration" class="log-duration">({{ log.duration }}ms)</span>
          </div>
        </div>
      </div>

    </el-card>
  </div>
</template>

<script>
export default {
  name: 'ArchitectureDemo',
  data() {
    return {
      loading: false,
      loginUser: null,
      currentSession: null,
      config: {
        sessionId: 1,
        seatInfo: '1排1座',
        concurrency: 20
      },
      stats: {
        total: 0,
        success: 0,
        fail: 0,
        avgTime: 0,
        totalTime: 0
      },
      logs: [],
      sessionInfo: {
        loaded: false,
        valid: false,
        movieName: '',
        playTime: '',
        hallName: ''
      }
    }
  },
  created() {
    const loginUserStr = window.sessionStorage.getItem('loginUser');
    if (loginUserStr) {
      this.loginUser = JSON.parse(loginUserStr);
    }
    // 页面加载时自动查询初始排片信息
    this.fetchSessionInfo();
  },
  methods: {
    async fetchSessionInfo() {
      try {
        const { data: res } = await this.axios.get(`sysSession/find/${this.config.sessionId}`);
        if (res.code === 200 && res.data) {
          const session = res.data;
          // 电影名称和影厅名称在嵌套对象中
          const movieName = session.sysMovie?.movieName || '未知电影';
          const hallName = session.sysHall?.hallName || '未知影厅';
          this.sessionInfo = {
            loaded: true,
            valid: true,
            movieName: movieName,
            playTime: session.sessionDate + ' ' + session.playTime,
            hallName: hallName
          };
        } else {
          this.sessionInfo = {
            loaded: true,
            valid: false,
            movieName: '',
            playTime: '',
            hallName: ''
          };
          this.$message.warning(`排片ID ${this.config.sessionId} 不存在，请检查输入`);
        }
      } catch (error) {
        this.sessionInfo = {
          loaded: true,
          valid: false,
          movieName: '',
          playTime: '',
          hallName: ''
        };
        this.$message.error('查询排片信息失败: ' + error.message);
      }
    },
    async cleanSeats() {
      try {
        this.addLog('SYSTEM', `正在重置场次 ${this.config.sessionId} 的数据...`, 'system');
        
        // 1. 获取场次详情
        const { data: sessionRes } = await this.axios.get(`sysSession/find/${this.config.sessionId}`);
        if (sessionRes.code !== 200) throw new Error('获取场次失败: ' + sessionRes.msg);
        
        const session = sessionRes.data;
        // 2. 获取影厅详情以恢复初始座位图
        const { data: hallRes } = await this.axios.get(`sysHall/${session.hallId}`);
        if (hallRes.code !== 200) throw new Error('获取影厅失败: ' + hallRes.msg);
        
        // 3. 重置数据
        session.sallNums = 0;
        session.sessionSeats = hallRes.data.seatState;
        
        // 4. 提交更新
        const { data: updateRes } = await this.axios.put('sysSession', session);
        if (updateRes.code === 200) {
          this.addLog('SUCCESS', '场次重置成功 (已售数清零，座位图已恢复)', 'success');
          this.$message.success('测试数据已重置');
        } else {
          this.addLog('ERROR', '重置失败: ' + updateRes.msg, 'error');
        }
      } catch (err) {
        this.addLog('ERROR', '重置操作异常: ' + err.message, 'error');
        this.$message.error('重置异常: ' + err.message);
      }
    },
    clearLogs() {
      this.logs = [];
    },
    resetStats() {
      this.stats = {
        total: 0,
        success: 0,
        fail: 0,
        avgTime: 0,
        totalTime: 0
      };
      this.clearLogs();
    },
    addLog(tag, message, type = 'info', duration = null) {
      const now = new Date();
      const timeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}.${now.getMilliseconds().toString().padStart(3, '0')}`;
      
      this.logs.push({
        time: timeStr,
        tag,
        message,
        type,
        duration
      });

      this.$nextTick(() => {
        const container = this.$refs.consoleBody;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    async startTest(mode) {
      if (this.loading) return;
      
      this.resetStats();
      this.loading = true;
      const count = this.config.concurrency;
      const isAsync = mode === 'async';
      const url = isAsync ? 'sysBill/async' : 'sysBill';
      
      this.addLog('SYSTEM', `开始 ${mode.toUpperCase()} 模式压力测试，并发数：${count}`, 'system');
      this.addLog('INFO', `模式说明: ${isAsync ? '异步队列模式 - 请求入队后顺序处理' : '同步模式 - 直接数据库操作'}`, 'info');

      try {
        // 先获取最新的场次状态
        const { data: res } = await this.axios.get(`sysSession/find/${this.config.sessionId}`);
        if (res.code === 200) {
          this.currentSession = res.data;
          this.addLog('INFO', `初始化成功，当前场次已售数: ${this.currentSession.sallNums}`, 'info');
        } else {
          throw new Error('获取场次状态失败');
        }

        const testStartTime = Date.now();
        const requests = [];
        for (let i = 0; i < count; i++) {
          requests.push(this.sendRequest(i + 1, url, isAsync));
        }

        await Promise.allSettled(requests);
        
        // 计算总耗时
        const totalDuration = Date.now() - testStartTime;
        
        // 输出最终执行结果汇总日志
        this.addLog('SYSTEM', '═══════════════════════════════════════════════════', 'system');
        this.addLog('SYSTEM', `📊 压力测试完成 - ${mode.toUpperCase()} 模式执行结果汇总`, 'system');
        this.addLog('SYSTEM', '───────────────────────────────────────────────────', 'system');
        this.addLog('INFO', `⏱  总耗时: ${totalDuration}ms`, 'info');
        this.addLog('INFO', `📈 总请求数: ${this.stats.total}`, 'info');
        this.addLog('SUCCESS', `✅ 成功: ${this.stats.success} 笔`, 'success');
        this.addLog('ERROR', `❌ 失败/冲突: ${this.stats.fail} 笔`, 'error');
        
        const successRate = this.stats.total > 0 
          ? ((this.stats.success / this.stats.total) * 100).toFixed(2) 
          : 0;
        this.addLog('INFO', `📊 成功率: ${successRate}%`, 'info');
        this.addLog('INFO', `⚡ 平均响应时间: ${this.stats.avgTime}ms`, 'info');
        
        // 根据结果给出分析
        if (isAsync) {
          if (this.stats.success === 1 && this.stats.fail === count - 1) {
            this.addLog('SUCCESS', '🎯 异步队列模式表现完美！只有1个请求成功购票，其余被正确拒绝，无超卖！', 'success');
          } else if (this.stats.success > 1) {
            this.addLog('ERROR', `⚠️ 警告：存在 ${this.stats.success} 笔成功，可能存在超卖风险！`, 'error');
          }
        } else {
          if (this.stats.success > 1) {
            this.addLog('ERROR', `⚠️ 同步模式下出现 ${this.stats.success} 笔成功购票，发生超卖！`, 'error');
          } else if (this.stats.success === 1) {
            this.addLog('INFO', '💡 同步模式本次未超卖，但高并发下仍有超卖风险', 'info');
          }
        }
        this.addLog('SYSTEM', '═══════════════════════════════════════════════════', 'system');
        
      } catch (error) {
        this.addLog('ERROR', '测试流程中断: ' + error.message, 'error');
      } finally {
        this.loading = false;
      }
    },
    async sendRequest(index, url, isAsync = false) {
      const startTime = Date.now();
      const userId = this.loginUser?.sysUser?.userId || 2;
      
      // 模拟座位锁定 payload
      let updatedSeats = this.currentSession.sessionSeats;
      try {
        const seatMatch = this.config.seatInfo.match(/(\d+)排(\d+)座/);
        if (seatMatch) {
          const row = seatMatch[1];
          const col = parseInt(seatMatch[2]);
          const seatObj = JSON.parse(updatedSeats);
          if (seatObj[row]) {
            seatObj[row][col - 1] = 1; 
            updatedSeats = JSON.stringify(seatObj);
          }
        }
      } catch (e) {
        console.error('Update seats fail', e);
      }

      const payload = {
        sysBill: {
          sessionId: this.config.sessionId,
          userId: userId,
          seats: JSON.stringify([this.config.seatInfo])
        },
        sessionSeats: updatedSeats
      };

      try {
        const response = await this.axios.post(url, payload);
        
        if (isAsync) {
          // 异步模式：获取 messageId 并开始轮询
          const messageId = response.data.data;
          this.addLog('QUEUED', `#${index} 已入队，等待处理... (ID: ${messageId.substring(0, 8)})`, 'info');
          await this.pollStatus(index, messageId, startTime);
        } else {
          // 同步模式：直接处理结果
          const duration = Date.now() - startTime;
          if (response.data && response.data.code === 200) {
             this.updateStats(true, duration);
             this.addLog('SUCCESS', `#${index} 请求成功: ${response.data.msg}`, 'success', duration);
          } else {
             // 处理业务逻辑错误 (返回 200 但 code 不为 200)
             this.updateStats(false, duration);
             this.addLog('FAIL', `#${index} 业务拒绝: ${response.data.msg || '未知错误'}`, 'error', duration);
          }
        }
      } catch (error) {
        const duration = Date.now() - startTime;
        this.updateStats(false, duration);
        
        // 提取后端返回的具体错误信息 (Spring Boot 全局异常处理返回的消息通常在 error.response.data 中)
        let errorMsg = error.message;
        if (error.response && error.response.data) {
          // 如果后端直接返回的是字符串消息
          if (typeof error.response.data === 'string') {
            errorMsg = error.response.data;
          } else if (error.response.data.msg) {
            errorMsg = error.response.data.msg;
          }
        }
        
        this.addLog('ERROR', `#${index} 失败: ${errorMsg}`, 'error', duration);
      }
    },
    updateStats(isSuccess, duration) {
      this.stats.total++;
      this.stats.totalTime += duration;
      this.stats.avgTime = (this.stats.totalTime / this.stats.total).toFixed(2);
      if (isSuccess) this.stats.success++;
      else this.stats.fail++;
    },
    async pollStatus(index, messageId, startTime) {
      // 轮询计数器，防止死循环
      let attempts = 0;
      const maxAttempts = 20; 
      
      return new Promise((resolve) => {
        const timer = setInterval(async () => {
          attempts++;
          try {
            const { data: res } = await this.axios.get(`sysBill/status/${messageId}`);
            const status = res.data;
            const duration = Date.now() - startTime;

            // 成功状态格式：success:{billId}
            if (status && typeof status === 'string' && status.startsWith('success:')) {
              clearInterval(timer);
              this.updateStats(true, duration);
              const billId = status.replace('success:', '');
              this.addLog('SUCCESS', `#${index} 异步处理成功，订单ID: ${billId} (耗时: ${duration}ms)`, 'success', duration);
              // 在真实业务场景中，这里可以跳转到支付页面：
              // this.$router.push(`/pay/${billId}`);
              resolve({ success: true, billId });
            } else if (status && typeof status === 'string' && status.startsWith('fail:')) {
              clearInterval(timer);
              this.updateStats(false, duration);
              const reason = status.replace('fail:', '');
              this.addLog('FAIL', `#${index} 异步处理失败: ${reason}`, 'error', duration);
              resolve({ success: false, reason });
            } else if (attempts >= maxAttempts) {
              clearInterval(timer);
              this.addLog('TIMEOUT', `#${index} 等待处理超时 (可能队列积压)`, 'warning', duration);
              resolve({ success: false, reason: 'timeout' });
            }
            // 如果是 'processing'，继续轮询
          } catch (e) {
            clearInterval(timer);
            this.addLog('ERROR', `#${index} 状态查询异常`, 'error');
            resolve({ success: false, reason: e.message });
          }
        }, 200);
      });
    }
  }
}
</script>

<style scoped>
.architecture-demo {
  padding: 20px;
  background-color: #f0f2f5;
  min-height: calc(100vh - 120px);
}
.breadcrumb { margin-bottom: 20px; }
.main-card { border-radius: 12px; }
.header-row { display: flex; justify-content: space-between; align-items: center; }
.page-title { font-size: 18px; font-weight: 600; display: flex; align-items: center; gap: 8px; }

.panel-card { border-radius: 8px; padding: 20px; border: 1px solid #ebeef5; height: 100%; }
.config-panel { background: #fafafa; }
.config-tip { font-size: 11px; color: #909399; margin-top: 10px; line-height: 1.4; }
.action-panel { background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%); color: white; border: none; }
.panel-title { font-size: 15px; font-weight: 600; margin-bottom: 15px; display: flex; align-items: center; gap: 8px; }
.action-panel .panel-title { color: #eee; }

.action-buttons { display: flex; gap: 15px; }
.action-group { flex: 1; display: flex; flex-direction: column; gap: 10px; }
.launch-btn { height: 80px; border-radius: 8px; border: none; transition: all 0.2s; }
.launch-btn:hover { transform: scale(1.02); }
.sync-btn { background: linear-gradient(to right, #ff4d4d, #f5222d); }
.async-btn { background: linear-gradient(to right, #52c41a, #389e0d); }
.btn-content { display: flex; flex-direction: column; align-items: center; }
.btn-title { font-size: 16px; font-weight: bold; }
.btn-subtitle { font-size: 10px; opacity: 0.7; }
.mode-desc { font-size: 11px; opacity: 0.8; text-align: center; margin: 0; }

.stats-section { margin-top: 20px; }
.stat-card { background: #fff; border-radius: 8px; padding: 15px; border: 1px solid #ebeef5; position: relative; overflow: hidden; }
.stat-label { font-size: 12px; color: #909399; }
.stat-value { font-size: 24px; font-weight: bold; margin-top: 5px; }
.stat-icon { position: absolute; right: 10px; bottom: 10px; font-size: 32px; opacity: 0.05; }
.total .stat-value { color: #409EFF; }
.success .stat-value { color: #67C23A; }
.fail .stat-value { color: #F56C6C; }
.time .stat-value { color: #E6A23C; }

.console-section { margin-top: 20px; background: #1e1e1e; border-radius: 8px; overflow: hidden; }
.console-header { background: #333; padding: 8px 15px; color: #aaa; font-size: 12px; display: flex; justify-content: space-between; align-items: center; }
.console-body { height: 280px; overflow-y: auto; padding: 10px; font-family: 'Consolas', monospace; font-size: 12px; color: #d4d4d4; }
.log-line { margin-bottom: 2px; display: flex; gap: 8px; }
.log-time { color: #569cd6; }
.log-tag { min-width: 50px; text-align: center; border-radius: 2px; font-weight: bold; font-size: 10px; }
.log-tag.system { background: #9c27b0; color: #fff; }
.log-tag.success { background: #4caf50; color: #fff; }
.log-tag.error { background: #f44336; color: #fff; }
.log-tag.info { background: #2196f3; color: #fff; }
.log-tag.warning { background: #ff9800; color: #fff; }
.log-duration { color: #e6a23c; margin-left: auto; }

/* 排片信息展示样式 */
.session-info-box {
  margin: 12px 0;
  padding: 12px;
  border-radius: 6px;
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border: 1px solid #a5d6a7;
}
.session-info-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.session-info-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
}
.session-info-item i {
  color: #43a047;
  font-size: 14px;
}
.info-label {
  color: #666;
  font-weight: 500;
}
.info-value {
  color: #2e7d32;
  font-weight: 600;
}
.session-info-error {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #e65100;
  font-size: 13px;
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  border: 1px solid #ffb74d;
  padding: 12px;
  border-radius: 6px;
  margin: 12px 0;
}
.session-info-error i {
  font-size: 16px;
}
</style>
